const RECOVERY_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function generateRecoveryCode() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const chars = Array.from(bytes).map((b) => RECOVERY_CODE_ALPHABET[b % 32]);
  const groups = [];
  for (let i = 0; i < 32; i += 4) groups.push(chars.slice(i, i + 4).join(''));
  return groups.join('-');
}

export function b64encode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

export function b64decode(str) {
  return Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
}

export function generateRsaKeyPair() {
  return crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt']
  );
}

export function importPublicKey(b64spki) {
  return crypto.subtle.importKey(
    'spki',
    b64decode(b64spki),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  );
}

// Wraps privateKey (must be extractable) with a PBKDF2-derived AES-CBC key.
// Returns { wrappedKey: ArrayBuffer, salt: Uint8Array, iv: Uint8Array }.
export async function wrapPrivateKeyWithPassword(password, privateKey) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  const wrappingKey = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-CBC', length: 256 },
    false,
    ['wrapKey']
  );
  const wrappedKey = await crypto.subtle.wrapKey('pkcs8', privateKey, wrappingKey, {
    name: 'AES-CBC',
    iv,
  });
  return { wrappedKey, salt, iv };
}

// RSA-OAEP + AES-CTR hybrid encryption of orgPrivBytes to recipientPublicKey.
// Returns "<b64(encAesKey)>:<b64(encOrgPriv)>" — caller prepends the "<id>:" prefix.
export async function hybridEncryptOrgKey(orgPrivBytes, recipientPublicKey) {
  const aesKeyBytes = crypto.getRandomValues(new Uint8Array(16));
  const aesIv = crypto.getRandomValues(new Uint8Array(16));
  const aesKey = await crypto.subtle.importKey('raw', aesKeyBytes, { name: 'AES-CTR' }, false, [
    'encrypt',
  ]);
  const aesData = `${b64encode(aesKeyBytes)}:${b64encode(aesIv)}`;
  const encOrgPriv = await crypto.subtle.encrypt(
    { name: 'AES-CTR', counter: aesIv, length: 128 },
    aesKey,
    orgPrivBytes
  );
  const encAesKey = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    recipientPublicKey,
    new TextEncoder().encode(aesData)
  );
  return `${b64encode(encAesKey)}:${b64encode(encOrgPriv)}`;
}

// Reads the org private key from localStorage and imports it as an extractable CryptoKey.
export async function loadOrgKeyFromStorage() {
  const b64 = localStorage.getItem('vault_key');
  if (!b64) throw new Error('Vault key not found in storage');
  return crypto.subtle.importKey(
    'pkcs8',
    b64decode(b64),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    true,
    ['decrypt']
  );
}
